/**
 * TagDialog — create a named tag (release / milestone) pointing at a commit.
 */

import { useState } from "react";
import {
  Badge,
  Box,
  Button,
  Callout,
  Dialog,
  Flex,
  ScrollArea,
  Select,
  Text,
  TextField,
} from "@radix-ui/themes";

import { useProvenanceContext } from "../../context/ProvenanceContext";
import { useCommits, useCreateTag, useTags, useDeleteTag } from "../../hooks/useVersioning";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export function TagDialog({ open, onOpenChange }: Props) {
  const { projectId, currentBranch } = useProvenanceContext();
  const { data: commits } = useCommits(projectId, currentBranch);
  const { data: existingTags } = useTags(projectId);
  const createMutation = useCreateTag(projectId);
  const deleteMutation = useDeleteTag(projectId);

  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [selectedCommit, setSelectedCommit] = useState("");
  const [success, setSuccess] = useState(false);

  const latestCommit = commits?.[0];

  function handleCreate() {
    const commitId = selectedCommit || latestCommit?.id;
    if (!commitId || !name.trim()) return;

    createMutation.mutate(
      { name: name.trim(), commitId, branch: currentBranch, message: message.trim() || undefined },
      {
        onSuccess: () => {
          setSuccess(true);
          setName("");
          setMessage("");
          setSelectedCommit("");
        },
      },
    );
  }

  function handleClose() {
    setSuccess(false);
    createMutation.reset();
    onOpenChange(false);
  }

  return (
    <Dialog.Root open={open} onOpenChange={handleClose}>
      <Dialog.Content maxWidth="500px">
        <Dialog.Title>Tags</Dialog.Title>
        <Dialog.Description size="2" color="gray">
          Create named milestones for your data. Tags are immutable pointers to commits.
        </Dialog.Description>

        {/* Existing tags */}
        {existingTags && existingTags.length > 0 && (
          <Box mt="3">
            <Text size="1" weight="medium" color="gray" mb="1" style={{ display: "block" }}>
              Existing tags
            </Text>
            <ScrollArea style={{ maxHeight: 150 }}>
              <Flex direction="column" gap="1">
                {existingTags.map((t) => (
                  <Flex key={t.id} align="center" justify="between" p="2" style={{
                    borderRadius: "var(--radius-1)",
                    border: "1px solid var(--gray-a3)",
                  }}>
                    <Flex align="center" gap="2">
                      <Badge size="1" color="blue">{t.name}</Badge>
                      <Text size="1" color="gray">{t.branch}</Text>
                      {t.message && <Text size="1" color="gray">— {t.message}</Text>}
                    </Flex>
                    <Flex align="center" gap="2">
                      <Text size="1" color="gray">{relativeTime(t.created_at)}</Text>
                      <Button
                        size="1"
                        variant="ghost"
                        color="red"
                        onClick={() => deleteMutation.mutate(t.name)}
                      >
                        Delete
                      </Button>
                    </Flex>
                  </Flex>
                ))}
              </Flex>
            </ScrollArea>
          </Box>
        )}

        {/* Create new tag */}
        <Flex direction="column" gap="3" mt="4">
          {success && (
            <Callout.Root color="green" size="1">
              <Callout.Text>Tag created successfully.</Callout.Text>
            </Callout.Root>
          )}

          <Box>
            <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>
              Tag name
            </Text>
            <TextField.Root
              placeholder="e.g. v1.0, baseline, milestone-2"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Box>

          <Box>
            <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>
              Message (optional)
            </Text>
            <TextField.Root
              placeholder="Description of this milestone"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </Box>

          <Box>
            <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>
              Commit
            </Text>
            <Select.Root value={selectedCommit || latestCommit?.id || ""} onValueChange={setSelectedCommit}>
              <Select.Trigger style={{ width: "100%" }} />
              <Select.Content>
                {commits?.map((c) => (
                  <Select.Item key={c.id} value={c.id}>
                    {c.message} ({c.id.slice(0, 8)}) — {relativeTime(c.created_at)}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </Box>
        </Flex>

        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray">Close</Button>
          </Dialog.Close>
          <Button
            onClick={handleCreate}
            disabled={!name.trim() || createMutation.isPending}
          >
            {createMutation.isPending ? "Creating..." : "Create Tag"}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
